// parking: one big button per screen.
//   Home    where + how long → "Find parking near me"
//   Plan    map, the spots to drive past (in order), the garage if they're all taken, ticket odds
//   Drive   one spot at a time: Navigate → "Parked here" or "Taken, next"
//   Parked  move-by time, what it costs, ticket odds, remind me, walk back
//   Check   "can I park right here?" for the curb you're standing at

import { getPlan, getCurb, getHere, geocode, report } from './api.js'
import { locate, lastKnown, quietly } from './geo.js'
import { prefs, savePrefs, recentPlaces, rememberPlace, drive, saveDrive, parked, saveParked } from './store.js'
import { local, fromLocal, addDays, when, clock, span, remindAt, nightBefore, MIN, HOUR, DAY } from '../src/shared/time.ts'
import { remindPush, cancelPush, retryCancels, registerWorker } from './push.js'
import { parkedRecord, moveBy, alertFor, remindArgs, deadlineLine, meterFirst } from './parked.js'
import {
  $, esc, plural, money, look, walkMin, SIDE, windowFor, durLabel, durChips, wireChips, navUrl, goAttrs, loopUrl, shareSpot, sideText, crossText, andList, oddsCard, eventNote, dayStrip, stripLabel, signLines, curbOdds, wireCurbOdds, sweepLine, areaSweep, sheet, setBar, busy, driveText, short, notesList, NAV_APPS,
} from './ui.js'

const app = document.getElementById('app')

let teardown = null // current screen's cleanup (maps, timers)
let mapMod = null
const mapLib = () => (mapMod ??= import('./map.js'))

document.querySelectorAll('[data-close]').forEach((b) => (b.onclick = () => $(b.dataset.close).close()))
// Tap outside a sheet closes it.
document.querySelectorAll('dialog').forEach((d) => d.addEventListener('click', (e) => { if (e.target === d) d.close() }))
// ---------------------------------------------------------------- home

let dest = null // {name, detail, lat, lon} or null for "near me"

function home() {
  setBar(null)
  const p = prefs()
  const pk = parked()
  const dr = drive()
  const resume = !pk && dr?.plan?.candidates?.length && Date.now() - dr.t < 2 * HOUR && dr.i < dr.plan.candidates.length ? dr : null
  const recent = recentPlaces()
  app.innerHTML = `
    <section class="home">
      ${resume ? `<a class="card tap parked-banner" href="#drive">
        <span class="badge badge-${look(resume.plan.candidates[resume.i])}" aria-hidden="true">${resume.i + 1}</span>
        <span class="who"><span class="where">Continue the loop</span>
        <span class="what">Spot ${resume.i + 1} of ${resume.plan.candidates.length}: ${esc(resume.plan.candidates[resume.i].street)}</span></span>
        <span class="chev" aria-hidden="true">›</span></a>` : ''}
      ${pk ? `<a class="card tap parked-banner" href="#parked">
        <span class="badge badge-ok" aria-hidden="true">P</span>
        <span class="who"><span class="where">Parked on ${esc(pk.curb.street)}</span>
        <span class="what">${esc(deadlineLine(pk, Date.now()))}</span></span>
        <span class="chev" aria-hidden="true">›</span></a>` : ''}
      <h1>Park in LA</h1>
      <p class="lede">A spot that's legal for your whole stay, and when you'll have to move.</p>
      <div class="search">
        <label for="q" class="field-label">Where to</label>
        <div class="search-box">
          <input id="q" type="search" placeholder="Near me, a place, or a map link" autocomplete="off" autocorrect="off" spellcheck="false" enterkeyhint="search" value="${esc(dest?.name ?? '')}">
          <button type="button" class="clear ${dest ? '' : 'hidden'}" id="clearQ" aria-label="Clear">×</button>
        </div>
        <div class="list suggest hidden" id="suggest" role="listbox" aria-label="Places"></div>
      </div>
      <p class="field-label">How long</p>
      ${durChips(p.dur)}
      <button type="button" class="block suite-primary" id="go">${dest ? `Find parking near ${esc(short(dest.name))}` : 'Find parking near me'}</button>
      <p class="error" id="err" role="alert"></p>
      <p class="fine as"><a href="#check" class="linkbtn">Already parked? Check your spot and ticket odds</a></p>
      ${recent.length && !dest ? `<div class="list-label"><b>Recent</b></div><div class="list">
        ${recent.map((r, i) => `<button type="button" class="card tap" data-recent="${i}">
          <span class="who"><span class="where">${esc(r.name)}</span>${r.detail ? `<span class="what">${esc(r.detail)}</span>` : ''}</span>
          <span class="chev" aria-hidden="true">›</span></button>`).join('')}</div>` : ''}
    </section>`

  // Pick a stay in place: re-rendering would throw away a half-typed search.
  wireChips(app, (dur) => {
    savePrefs({ dur })
    app.querySelectorAll('.chips [role=radio]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.dur === String(dur))))
    const until = app.querySelector('.chip-btn.until')
    const custom = typeof dur === 'string' && dur.startsWith('until:')
    until.dataset.on = String(custom)
    until.querySelector('span').textContent = custom ? durLabel(dur) : 'Until…'
  })
  const q = $('q'), sug = $('suggest')
  let seq = 0, timer = 0
  let goWhenReady = false // Go was tapped before the matches for what's typed came back
  q.oninput = () => {
    dest = null
    goWhenReady = false
    const text = q.value.trim()
    // Typed a place: the big button goes there (the first match), not to where you're standing.
    $('go').textContent = text.length >= 3 ? 'Find parking there' : 'Find parking near me'
    $('clearQ').classList.toggle('hidden', !q.value)
    clearTimeout(timer)
    if (text.length < 3) { sug.classList.add('hidden'); return }
    // As you type it asks Photon only. If Photon is down, the backup (Nominatim) allows no
    // autocomplete, so it's asked once you've stopped typing for a second.
    const run = async (mine, done) => {
      try {
        const { results, note } = await geocode(text, lastKnown(), done)
        if (mine !== seq) return
        // A pasted map link or coordinates: that's the place, no list to pick from.
        if (results.length === 1 && results[0].exact && !goWhenReady) { pick(results[0]); return }
        sug.innerHTML = results.length
          ? results.map((r, i) => `<button type="button" class="card tap" role="option" data-i="${i}"><span class="who"><span class="where">${esc(r.name)}</span>${r.detail ? `<span class="what">${esc(r.detail)}</span>` : ''}</span></button>`).join('')
          : `<p class="empty">${esc(note || 'No places in LA match that.')}</p>`
        sug.classList.remove('hidden')
        sug.dataset.for = text // these matches are for this text, not something typed since
        sug.dataset.note = note || ''
        sug.querySelectorAll('[data-i]').forEach((b) => (b.onclick = () => pick(results[Number(b.dataset.i)])))
        if (goWhenReady) {
          goWhenReady = false
          $('err').textContent = ''
          if (results.length) { sug.querySelector('[data-i]').click(); $('go').click() }
        }
      } catch (e) {
        if (mine !== seq) return
        if (goWhenReady) $('err').textContent = ''
        goWhenReady = false
        if (!done && /busy/.test(e.message)) { timer = setTimeout(() => mine === seq && run(mine, true), 1000); return }
        sug.innerHTML = `<p class="empty">${esc(e.message)}</p>`
        sug.classList.remove('hidden')
      }
    }
    timer = setTimeout(() => run(++seq, false), 280)
  }
  q.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); $('go').click() } }
  $('clearQ').onclick = () => { dest = null; home(); $('q').focus() }
  const pick = (r) => { dest = r; home() }
  app.querySelectorAll('[data-recent]').forEach((b) => (b.onclick = () => pick(recent[Number(b.dataset.recent)])))

  // Warm MapLibre while you type, so the plan's map shows up at once.
  ;(window.requestIdleCallback ?? ((f) => setTimeout(f, 1200)))(() => mapLib().then((m) => m.preload()))

  $('go').onclick = async () => {
    $('err').textContent = ''
    const text = q.value.trim()
    if (!dest && text.length >= 3) {
      // The first match for what's typed now: picked (home re-renders with it), then planned.
      const first = sug.dataset.for === text && sug.querySelector('[data-i]')
      if (first) { first.click(); $('go').click(); return }
      goWhenReady = true // still looking it up: go as soon as the matches arrive
      $('err').textContent = sug.dataset.for === text ? sug.dataset.note || 'No places in LA match that. Try another name, or clear the box to find parking near you.' : 'Looking that up…'
      if (sug.dataset.for === text) goWhenReady = false
      return
    }
    if (dest) {
      rememberPlace(dest)
      location.hash = `plan?lat=${dest.lat.toFixed(6)}&lon=${dest.lon.toFixed(6)}&name=${encodeURIComponent(dest.name)}`
      return
    }
    const done = busy($('go'), 'Finding you…')
    try {
      const me = await locate({ fresh: true })
      location.hash = `plan?lat=${me.lat.toFixed(6)}&lon=${me.lon.toFixed(6)}&here=1`
    } catch (e) {
      done()
      $('err').textContent = e.message
      q.focus()
    }
  }
}


// ---------------------------------------------------------------- plan

let lastPlan = null // { key, plan, at }

async function planScreen(params) {
  teardown?.()
  teardown = null
  const at = { lat: Number(params.get('lat')), lon: Number(params.get('lon')) }
  const here = params.get('here') === '1'
  const name = params.get('name')
  // A shared plan carries its stay ("Share this plan"): adopt it, then drop it from the URL.
  const sharedDur = params.get('dur')
  const okDur = parseDur(sharedDur)
  if (sharedDur && okDur === null) { params.delete('dur'); history.replaceState(null, '', '#plan?' + params.toString()) }
  if (okDur !== null) {
    savePrefs({ dur: okDur })
    params.delete('dur')
    history.replaceState(null, '', '#plan?' + params.toString())
  }
  const p = prefs()
  // A stay for this plan only ("Plan until 2:30am"): it rides in the URL and isn't saved as your usual.
  const dur = parseDur(params.get('stay')) ?? p.dur
  const startAt = Number(params.get('start') || 0)
  const arrive = startAt > Date.now() ? Math.round((startAt - Date.now()) / MIN) : Number(params.get('arrive') || 0)
  const win = windowFor(dur, startAt > Date.now() ? startAt : Date.now() + arrive * MIN)
  const key = `${at.lat},${at.lon},${dur},${startAt || arrive},${Math.round(win.end / (5 * MIN))},${p.freeOnly ? 1 : 0}`
  const title = (pl) => (name ? short(name) : pl?.place ? pl.place.street : 'you')

  app.innerHTML = `<section class="plan">
    ${planHead('Looking around ' + esc(name ? short(name) : 'you') + '…', dur, win, arrive)}
    <div class="mapbox"><div id="map" class="map" aria-label="Map of nearby curbs"></div></div>
    <div id="planBody"><p class="loading"><span class="spinner"></span> Reading the curbs…</p></div>
  </section>`
  wirePlanHead(params, dur)
  setBar(null)

  // Build the map while the plan is computed; draw on it when both are ready.
  let ctx = null
  let disposed = false
  teardown = () => { disposed = true; ctx?.map.remove() }
  const mapReady = mapLib().then((m) => m.createMap($('map'), { center: at }).then((c) => ({ m, c }))).catch((e) => {
    console.warn('map', e)
    return null
  })

  let plan
  try {
    if (lastPlan?.key === key && Date.now() - lastPlan.t < 90_000) plan = lastPlan.plan
    else {
      // Where you're driving from decides the loop's order; only ask if it's already allowed.
      const origin = here ? at : (lastKnown() ?? (await quietly()))
      plan = await getPlan(at, win, origin, p.freeOnly)
      lastPlan = { key, plan, t: Date.now() }
    }
  } catch (e) {
    if (!disposed) $('planBody').innerHTML = `<p class="error">${esc(e.message)}</p><p><a class="button quiet" href="#">Back</a></p>`
    // The map started loading before the plan: hand it to teardown, or remove it if that's been and gone.
    mapReady.then((r) => { if (r) { if (disposed) r.c.map.remove(); else ctx = r.c } })
    return
  }
  // Navigated away, or a newer load (another stay, Free only) replaced this one.
  if (disposed) { mapReady.then((r) => r?.c.map.remove()); return }

  app.querySelector('.plan-head h1').textContent = here ? `Near you` : `Near ${title(plan)}`
  if (here && plan.place) app.querySelector('.plan-sub').textContent = `${plan.place.street}${plan.place.cross ? ', ' + crossText(plan.place.cross) : ''}`
  renderPlanBody(plan, win, { here, name, dur, stale: startAt && startAt < Date.now() - 5 * MIN ? startAt : 0, loop: `${at.lat},${at.lon},${dur},${startAt || arrive},${p.freeOnly ? 1 : 0}` })
  // Rows work at once; `ctx` is read when tapped, so they focus the map once it's there.
  app.querySelectorAll('[data-cand]').forEach((b) => (b.onclick = () => openCandidate(plan, Number(b.dataset.cand), win, ctx)))

  // The list is usable already; the map joins when MapLibre is ready.
  const ready = await mapReady
  if (ready) ctx = ready.c
  if (disposed) { ctx?.map.remove(); return }
  try {
    if (!ready) throw new Error('map unavailable')
    const m = ready.m
    m.showPlan(ctx, plan, {
      me: here ? null : lastKnown(),
      onCandidate: (id) => openCandidate(plan, id, win, ctx),
      onGarage: (id) => openGarage(plan, id, win),
      onCurb: (id) => openCurb(id, win, ctx),
    })
    app.querySelector('.mapbox').classList.add('ready')
    // Closing a spot's sheet zooms back out to the whole loop.
    const back = () => { if (!disposed) m.overview(ctx, plan, here ? null : lastKnown()) }
    $('sheet').addEventListener('close', back)
    const end = teardown
    teardown = () => { $('sheet').removeEventListener('close', back); end?.() }
    // "Ticket heat" (block-by-block odds over the map): the checkbox under the map, remembered.
    const heat = $('heatOn')
    if (heat) {
      const apply = () => m.setHeat(ctx, heat.checked)
      heat.addEventListener('change', apply)
      apply()
    }
  } catch (e) {
    console.warn('map', e)
    $('map').innerHTML = `<p class="map-fail">Map unavailable. The list below still works.</p>`
  }
}

function planHead(h1, dur, win, arrive) {
  return `<div class="plan-head">
    <a class="back" href="#" aria-label="Back">‹</a>
    <div class="plan-title"><h1>${h1}</h1><p class="plan-sub"></p></div>
    <button type="button" class="when-btn" id="whenBtn" aria-label="Change when"><b>${arrive ? `${esc(when(win.start, Date.now()))} · ` : ''}${esc(durLabel(dur))}</b><small>${typeof dur === 'string' && /^(until|end):/.test(dur) ? esc(span(win.end - win.start)) : `until ${esc(when(win.end, Date.now()))}`}</small></button>
  </div>`
}

/** A stay from a URL, if it's one the app itself makes: 10 min to 3 days, overnight, or until a time of day. */
function parseDur(s) {
  if (/^\d+$/.test(s || '')) return Number(s) >= 10 && Number(s) <= 72 * 60 ? Number(s) : null
  if (s === 'night' || (/^until:\d+$/.test(s || '') && Number(s.slice(6)) < 1440)) return s
  // A fixed end, for one plan: still ahead (at least 10 minutes) and within 3 days.
  if (/^end:\d+$/.test(s || '')) { const t = Number(s.slice(4)); return t > Date.now() + 10 * MIN && t < Date.now() + 72 * HOUR ? s : null }
  return null
}

function wirePlanHead(params, dur) {
  $('whenBtn').onclick = () => {
    const now = Date.now()
    const start = Number(params.get('start') || 0)
    const later = start > now + 5 * MIN ? start : 0 // a picked day and time, not one of the chips
    const arrive = later ? -1 : Number(params.get('arrive') || 0)
    // datetime-local values are LA wall-clock time, whatever timezone the phone is in.
    const pad = (n) => String(n).padStart(2, '0')
    const iso = (ms) => { const l = local(ms); return `${l.date}T${pad(Math.floor(l.min / 60))}:${pad(l.min % 60)}` }
    $('whenBody').innerHTML = `
      <p class="field-label">Arriving</p>
      <div class="chips" role="radiogroup" aria-label="Arriving">
        ${[[0, 'Now'], [15, 'In 15 min'], [30, 'In 30 min'], [60, 'In 1 hr'], [120, 'In 2 hr']].map(([m, l]) => `<button type="button" class="chip-btn" role="radio" aria-checked="${m === arrive}" data-arrive="${m}">${l}</button>`).join('')}
        <label class="chip-btn until" data-on="${!!later}"><span>${later ? esc(when(later, now)) : 'Pick a time…'}</span><input type="datetime-local" id="startPick" step="900" min="${iso(Math.floor(now / (15 * MIN)) * 15 * MIN)}" max="${iso(now + 30 * DAY)}" value="${iso(later || now + DAY)}" aria-label="Arriving at another time or day"></label>
      </div>
      <p class="field-label">Staying</p>
      ${durChips(dur)}`
    $('when').showModal()
    const apply = () => { $('when').close(); lastPlan = null; planScreen(params) }
    wireChips($('whenBody'), (d) => {
      savePrefs({ dur: d })
      params.delete('stay')
      history.replaceState(null, '', '#plan?' + params.toString())
      apply()
    })
    $('startPick').onchange = (e) => {
      const [date, hm] = e.target.value.split('T')
      if (!date || !hm) return
      const [h, m] = hm.split(':').map(Number)
      const ms = fromLocal(date, h * 60 + m)
      if (!(ms > Date.now())) return
      params.set('start', String(ms))
      params.delete('arrive')
      history.replaceState(null, '', '#plan?' + params.toString())
      apply()
    }
    $('whenBody').querySelectorAll('[data-arrive]').forEach((b) => (b.onclick = () => {
      params.set('arrive', b.dataset.arrive)
      params.delete('start')
      history.replaceState(null, '', '#plan?' + params.toString())
      apply()
    }))
  }
}

/** "Navigate", or "Send to Tesla" when that's your navigation (the share sheet hands it to the car). */
const navLabel = (what = '') => (prefs().nav === 'tesla' && navigator.share ? `Send ${what || 'it'} to Tesla` : what ? `Navigate to ${what}` : 'Navigate')

/** "Or one route past all 5 in Apple Maps": every spot as a stop, ending at the garage. */
function loopLine(spots, garage, rest = false) {
  const l = loopUrl(spots, garage)
  if (!l || spots.length < 2) return ''
  const [url, app] = l
  const what = rest ? `the ${spots.length} spots left` : `all ${spots.length} spots`
  return `<p class="fine"><a href="${url}" target="_blank" rel="noopener">Or one route past ${what} in ${app}</a>${garage ? ', ending at the garage' : ''}</p>`
}

/** What to expect from the loop: "Expect about 900 m of driving and a 3 min walk." */
function loopNote(plan) {
  const l = plan.loop
  if (!l || plan.candidates.length < 2) return ''
  const km = l.drive < 950 ? `${Math.max(100, Math.round(l.drive / 100) * 100)} m` : `${(l.drive / 1000).toFixed(1)} km`
  const all = Math.round(l.garage * 100)
  const tail = all >= 1 ? `, and a ${all}% chance they're all taken` : ''
  return `<p class="fine loop-note">Expect about ${km} of driving and a ${walkMin(l.walk)} min walk${tail}.</p>`
}

function candRow(c, now) {
  const notes = [`${walkMin(c.walk)} min walk`, ...c.notes.slice(0, 1)].join(' · ')
  return `<button type="button" class="card tap cand" data-cand="${c.id}">
    <span class="badge badge-${look(c)}" aria-hidden="true">${c.n}</span>
    <span class="who">
      <span class="where">${esc(c.street)}${c.side ? ` <span class="side">${sideText(c)}</span>` : ''}</span>
      <span class="what">${esc(c.why)}</span>
      <span class="note">${esc(notes)}</span>
    </span>
    ${c.live ? `<span class="live-chip ${c.live.vacant ? 'yes' : 'no'}">${c.live.vacant ? `${c.live.vacant} open` : 'full'}</span>` : ''}
    <span class="chev" aria-hidden="true">›</span>
  </button>`
}

const cents = (t) => t.replace(/\$(\d+)\.(\d)(?!\d)/g, '$$$1.$20') // "From $0.5" -> "From $0.50"

function garageRow(g, i) {
  // An unpriced garage's first rate line is often for other hours ("Each 10 Min (2am-5pm): $4"), so
  // only a plain "From $10" is shown; otherwise say it isn't priced.
  const from = g.rates.find((t) => /^from \$/i.test(t))
  const bits = [g.est !== null ? `about ${money(g.est)}` : from ? cents(from) : g.op === 'ParkMobile' ? 'rates in the ParkMobile app' : g.rates.length ? 'price for your stay unknown' : 'rates unknown', `${walkMin(g.walk)} min walk`]
  if (g.closes) bits.push(`closes ${clock(local(g.closes).min)}`)
  else if (g.open === true) bits.push('open your whole stay')
  else bits.push('hours unknown')
  return `<button type="button" class="card tap" data-garage="${g.id}">
    <span class="badge badge-garage" aria-hidden="true">P</span>
    <span class="who"><span class="where">${esc(g.name)}</span><span class="what">${esc(bits.join(' · '))}</span></span>
    <span class="chev" aria-hidden="true">›</span></button>`
}

/** Yellow curbs turning free soon are worth waiting for when what's free now isn't (mostly paid meters). */
function yellowWorthIt(plan, c) {
  if (!plan.yellowSoon || c.some((x) => x.kind === 'yellow')) return false
  return !c.length || c.filter((x) => x.cost > 0).length >= c.length / 2
}

/** Nothing lasts the stay: "212 curbs nearby work until 2:30am, then no parking." and a button for that. */
function shorterNudge(plan, c) {
  const s = plan.shorter
  if (c.length || !s || (prefs().freeOnly && plan.counts.paidOk)) return ''
  const why = s.why ? (/limit$/.test(s.why) ? `, then the ${s.why} runs out` : `, then ${s.why.toLowerCase()}`) : ''
  const t = when(s.until, plan.now)
  const part = `${span(s.until - plan.start)} of your ${span(plan.end - plan.start)}`
  return `<div class="nudge plain"><p><b>${plural(s.count, 'curb')} nearby ${s.count === 1 ? 'works' : 'work'} until ${esc(t)}</b> (${esc(part)})${esc(why)}.</p><button type="button" class="quiet" id="planShorter">Plan until ${esc(t)}</button></div>`
}

function renderPlanBody(plan, win, { here, name, dur, stale, loop }) {
  const c = plan.candidates
  const yellow = c.filter((x) => x.kind === 'yellow').length
  const liveOpen = c.filter((x) => x.live?.vacant).length
  let summary
  if (!c.length && plan.coverage === 'thin') summary = `Couldn't find street parking here for your whole stay${plan.garages.length ? ', so here are garages' : ''}.`
  else if (!c.length && prefs().freeOnly && plan.counts.paidOk) summary = `No free spot lasts your whole stay, but ${plural(plan.counts.paidOk, 'meter')} nearby ${plan.counts.paidOk === 1 ? 'does' : 'do'}.`
  else if (!c.length) summary = `Nothing on the street works for your whole stay within a 15-minute walk.`
  else if (plan.coverage === 'thin') summary = 'Check each sign here.'
  if (c.length) {
    summary = (plan.coverage === 'thin' ? summary + ' ' : '') + (c.length === 1 ? '1 spot to drive past.' : `${plural(c.length, 'spot')} to drive past, in order.`)
    // Name them by number: "1 is a yellow curb" reads as "spot 1 is".
    const nums = (xs) => andList(xs.map((x) => String(x.n)))
    const ys = c.filter((x) => x.kind === 'yellow'), ls = c.filter((x) => x.live?.vacant)
    if (yellow) summary += yellow === c.length ? ` ${yellow === 1 ? "It's a yellow curb" : 'All are yellow curbs'}, free for your stay.` : ` ${yellow === 1 ? `Spot ${nums(ys)} is a yellow curb` : `Spots ${nums(ys)} are yellow curbs`}, free for your stay.`
    if (liveOpen) summary += liveOpen === c.length ? ` ${liveOpen === 1 ? 'It has' : 'All have'} an empty metered space right now.` : ` ${liveOpen === 1 ? `Spot ${nums(ls)} has` : `Spots ${nums(ls)} have`} an empty metered space right now.`
  }
  // Sensor meters nearby, as a read on how busy the street is right now.
  const a = plan.area
  const busyLine = a ? (a.vacant / a.total < 0.2 ? `Busy right now: only ${a.vacant} of ${a.total} meters nearby are open.`
    : a.vacant / a.total > 0.5 ? `Quiet right now: ${a.vacant} of ${a.total} meters nearby are open.` : '') : ''
  const g = plan.garages
  $('planBody').innerHTML = `
    <div class="legend" aria-hidden="true">
      <span><i class="sw sw-yellow"></i>Yellow, free</span><span><i class="sw sw-go"></i>Free</span><span><i class="sw sw-pay"></i>Meter</span><span><i class="sw sw-wait"></i>Opens soon</span>${plan.map.live.length ? '<span><i class="dot dot-live"></i>Open now</span>' : ''}<span class="heat-key" hidden><i class="sw sw-heat"></i>Ticket odds per block, low to high</span>
    </div>
    ${stale ? `<p class="busy">That plan was for ${esc(when(stale, Date.now()))}, which has passed, so this is for now.</p>` : ''}
    <p class="summary">${esc(summary)}</p>
    ${busyLine ? `<p class="busy">${esc(busyLine)}</p>` : ''}
    ${plan.cityBan ? `<p class="warn">${esc(plan.cityBan)}</p>` : ''}
    ${eventNote(plan.events, plan.now)}
    <label class="toggle"><input type="checkbox" id="freeOnly" ${prefs().freeOnly ? 'checked' : ''}> Free spots only, no meters</label>
    ${plan.map.heat?.length ? `<label class="toggle"><input type="checkbox" id="heatOn" ${prefs().heat ? 'checked' : ''}> Shade the map by ticket odds</label>` : ''}
    ${yellowWorthIt(plan, c) ? `<div class="nudge"><span class="badge badge-yellow" aria-hidden="true"></span><p><b>${plural(plan.yellowSoon.count, 'yellow curb')} nearby turn${plan.yellowSoon.count === 1 ? 's' : ''} free at ${esc(clock(local(plan.yellowSoon.at).min))}</b> and stay free for your ${esc(span(win.end - win.start))}.</p><button type="button" class="quiet" id="planLater">Plan for ${esc(clock(local(plan.yellowSoon.at).min))}</button></div>` : ''}
    ${shorterNudge(plan, c)}
    ${c.length ? `<div class="list">${c.map((x) => candRow(x, plan.now)).join('')}</div>${loopNote(plan)}` : ''}
    ${g.length ? `<div class="list-label"><b>${c.length ? "If they're all taken" : 'Garages nearby'}</b></div><div class="list">${g.slice(0, c.length ? 2 : 4).map(garageRow).join('')}</div>` : (c.length ? '' : '<p class="empty">No garages we know of nearby either.</p>')}
    ${oddsCard(plan.risk, { win })}
    <p class="fine sources">The posted sign always wins. <button type="button" class="linkbtn" id="sharePlan">Share this plan</button></p>`
  app.querySelectorAll('[data-garage]').forEach((b) => (b.onclick = () => openGarage(plan, Number(b.dataset.garage), win)))
  // The map applies it once it's ready (planScreen); the legend and the preference follow here.
  const heatKey = app.querySelector('.heat-key')
  if (heatKey) heatKey.hidden = !$('heatOn')?.checked
  $('heatOn')?.addEventListener('change', (e) => {
    savePrefs({ heat: e.target.checked })
    if (heatKey) heatKey.hidden = !e.target.checked
  })
  $('freeOnly').onchange = (e) => {
    savePrefs({ freeOnly: e.target.checked })
    lastPlan = null
    planScreen(new URLSearchParams(location.hash.split('?')[1] || ''))
  }
  $('sharePlan').onclick = async (e) => {
    // The same plan for someone else: place, arrival (if it isn't "now") and stay.
    // ~10 m is plenty for a parking plan, and it doesn't hand out the sharer's exact spot.
    const q = new URLSearchParams({ lat: plan.at.lat.toFixed(4), lon: plan.at.lon.toFixed(4) })
    // "Near you" means the sharer: the link names the street, or at least that it's a shared spot.
    const place = name || plan.place?.street || 'a shared spot'
    q.set('name', place)
    if (win.start > Date.now() + 5 * MIN) q.set('start', String(win.start))
    q.set('stay', String(dur)) // this plan's stay, without changing the recipient's usual one
    const url = `${location.origin}/#plan?${q.toString()}`
    const d = dur
    const text = `Parking near ${place}, ${win.start > Date.now() + 5 * MIN ? when(win.start, Date.now()) : 'now'} ${typeof d === 'string' ? durLabel(d).toLowerCase() : `for ${durLabel(d)}`}`
    try {
      if (navigator.share) await navigator.share({ title: 'Parking plan', text, url })
      else { await navigator.clipboard.writeText(`${text}: ${url}`); e.target.textContent = 'Link copied' }
    } catch {}
  }
  $('planShorter')?.addEventListener('click', () => {
    // Shorten this plan's stay to what works, as a fixed end (not your usual stay, and it can't roll
    // over to tomorrow on a later reload).
    const q = new URLSearchParams(location.hash.split('?')[1] || '')
    q.set('stay', `end:${plan.shorter.until}`)
    location.hash = 'plan?' + q.toString()
  })
  $('planLater')?.addEventListener('click', () => {
    const at = plan.yellowSoon.at
    const q = new URLSearchParams(location.hash.split('?')[1] || '')
    q.delete('arrive')
    q.set('start', String(at))
    // "Until 6:10pm" planned from 6pm would roll to tomorrow: keep the stay's length instead.
    if (typeof dur === 'string' && dur.startsWith('until:') && fromLocal(local(at).date, Number(dur.slice(6))) < at + 15 * MIN) q.set('stay', String(Math.round((win.end - win.start) / MIN)))
    location.hash = 'plan?' + q.toString()
  })
  if (c.length) {
    // Back on the plan mid-loop (a look at the list): carry on from the spot you were on.
    const dr = drive()
    // Only the same plan: place, stay, arrival and Free only (the hash alone misses a changed stay).
    const resume = dr && dr.key === loop && dr.i > 0 && dr.i < (dr.plan?.candidates?.length ?? 0) && Date.now() - dr.t < 2 * HOUR
    const start = () => {
      saveDrive({ plan: slimPlan(plan), win, i: 0, t: Date.now(), back: location.hash, key: loop })
      location.hash = 'drive'
    }
    setBar(`<div class="bar-inner"><button type="button" class="primary" id="startLoop">${resume ? `Continue: spot ${dr.i + 1} of ${dr.plan.candidates.length}` : `Drive past ${plural(c.length, 'spot')}`}</button>${g[0] ? `<a class="quiet button" ${goAttrs(g[0], g[0].name)}>Garage</a>` : ''}</div>${resume ? '<p class="fine"><button type="button" class="linkbtn" id="restartLoop">Start over from spot 1</button></p>' : loopLine(c, g[0])}`)
    $('startLoop').onclick = () => { if (resume) location.hash = 'drive'; else start() }
    $('restartLoop')?.addEventListener('click', start)
  } else if (g[0]) {
    setBar(`<div class="bar-inner"><a class="primary button" ${goAttrs(g[0], g[0].name)}>Go to ${esc(short(g[0].name))}</a></div>`)
  }
}

function slimPlan(plan) {
  const { map, ...rest } = plan
  return rest
}

async function openCandidate(plan, id, win, ctx) {
  const c = plan.candidates.find((x) => x.id === id)
  if (!c) return
  if (ctx) mapLib().then((m) => m.focusCurb(ctx, c.geom, { color: undefined }))
  sheet(candidateSheet(c, null, win))
  wireCandidateSheet(c, win)
  try {
    const d = await getCurb(id, win)
    if ($('sheet').open && $('sheetBody').querySelector('[data-curb]')?.dataset.curb === String(id)) {
      sheet(candidateSheet(c, d, win))
      wireCandidateSheet(c, win)
      wireCurbOdds($('sheetBody'), win)
    }
  } catch {}
}

function candidateSheet(c, d, win) {
  const verdict = c.wait > 0 ? `Opens ${when(c.from, win.start)}` : c.cost > 0 ? `About ${money(c.cost)} for your stay` : 'Free for your whole stay'
  const until = c.stopKind === 'horizon' ? 'No time limit' : `Move by ${when(c.stopAt, win.start)}`
  return `<div data-curb="${c.id}"></div>
    <div class="sheet-head"><span class="badge badge-${look(c)}">${c.n}</span><div><h2>${esc(c.street)}</h2>
    <p class="muted">${esc([sideText(c), crossText(c.cross)].filter(Boolean).join(' · '))}</p></div></div>
    <p class="verdict ok">${esc(verdict)}</p>
    <p class="muted">${esc(until)}${c.stopKind !== 'horizon' && c.stopWhy ? ` (${esc(c.stopWhy.toLowerCase())})` : ''} · ${walkMin(c.walk)} min walk · about ${plural(c.spaces, 'space')}</p>
    ${c.cost === 0 && d?.verdict.meterOn && (c.stopKind === 'horizon' || d.verdict.meterOn < c.stopAt) ? `<p class="muted">The meter starts at ${esc(when(d.verdict.meterOn, win.start))}, if you stay that long.</p>` : ''}
    ${notesList(c.notes)}
    ${d ? `${sweepLine({ ...d, sweepSkip: null })}<p class="field-label">${esc(stripLabel(d.day))}</p>${dayStrip(d.day, win)}${curbOdds(d.risk, d.riskHours, d.day, win)}<p class="field-label">What the sign says</p>${signLines(d.lines)}` : '<p class="loading"><span class="spinner"></span> Loading the sign…</p>'}
    <div class="sheet-actions">
      <a class="button block suite-primary" ${goAttrs(c)} id="navC">${navLabel()}</a>
      <div class="two"><button type="button" class="quiet" id="takenC">It's taken</button><button type="button" class="quiet" id="parkC">I parked here</button></div>
    </div>
    <p class="fine">${c.assumed || c.disputed ? 'Check the posted sign for this curb\'s hours.' : 'Rules can change. The posted sign wins.'} <button type="button" class="linkbtn" id="goneC">Sign doesn't match?</button></p>`
}



function wireCandidateSheet(c, win) {
  $('takenC').onclick = () => { report(c.id, 'taken'); $('takenC').textContent = 'Thanks'; $('takenC').disabled = true }
  $('goneC').onclick = () => { report(c.id, 'gone'); $('goneC').replaceWith(Object.assign(document.createElement('span'), { textContent: "Thanks. We'll warn the next person." })) }
  $('parkC').onclick = () => { $('sheet').close(); parkAt(c, win) }
}

async function openCurb(id, win, ctx) {
  sheet(`<p class="loading" data-curb-loading="${id}"><span class="spinner"></span> Loading the sign…</p>`)
  const screen = location.hash
  try {
    const d = await getCurb(id, win)
    // Closed the sheet, or left the screen, while it loaded: don't pop it back up.
    if (!$('sheet').open || location.hash !== screen || !$('sheetBody').querySelector(`[data-curb-loading="${id}"]`)) return
    if (ctx) mapLib().then((m) => m.focusCurb(ctx, d.curb.geom))
    const v = d.verdict
    const ok = v.ok
    const head = ok ? (v.cost > 0 ? `OK for your stay, about ${money(v.cost)}` : 'Free for your whole stay') : notNow(v, win)
    sheet(`<div class="sheet-head"><span class="badge badge-${look({ kind: d.curb.kind, cost: d.verdict.cost })}" aria-hidden="true"></span><div><h2>${esc(d.curb.street || "Unnamed street")}</h2>
      <p class="muted">${esc([sideText(d.curb), crossText(d.curb.cross)].filter(Boolean).join(' · '))}</p></div></div>
      <p class="verdict ${ok ? 'ok' : 'no'}">${esc(head)}</p>
      ${ok && v.cost === 0 && v.meterOn && (v.stopKind === 'horizon' || v.meterOn < v.stopAt) ? `<p class="muted">The meter starts at ${esc(when(v.meterOn, win.start))}, if you stay that long.</p>` : ''}
      ${ok && v.stopKind !== 'horizon' ? `<p class="muted">Move by ${esc(when(v.stopAt, win.start))} (${esc(v.stopWhy.toLowerCase())})</p>` : ''}
      ${d.live ? `<p class="muted">${d.live.vacant} of ${d.live.total} meters open right now</p>` : ''}
      ${sweepLine(d)}
      <p class="field-label">${esc(stripLabel(d.day))}</p>${dayStrip(d.day, win)}
      ${curbOdds(d.risk, d.riskHours, d.day, win)}
      <p class="field-label">What the sign says</p>${signLines(d.lines)}
      <div class="sheet-actions"><a class="button block quiet" ${goAttrs(d.curb)}>${navLabel()}</a>
      ${ok ? '<button type="button" class="block suite-primary" id="parkHere">I parked here</button>' : ''}</div>
      <p class="fine">Rules can change. The posted sign wins.</p>`)
    if (ok) $('parkHere').onclick = () => { $('sheet').close(); parkAt({ ...d.curb, stopAt: v.stopAt, stopWhy: v.stopWhy, stopKind: v.stopKind, cost: v.cost }, win) }
    wireCurbOdds($('sheetBody'), win)
  } catch (e) {
    if (!$('sheet').open || location.hash !== screen || !$('sheetBody').querySelector(`[data-curb-loading="${id}"]`)) return
    sheet(`<p class="error">${esc(e.message)}</p>`)
  }
}

function openGarage(plan, id, win) {
  const g = plan.garages.find((x) => x.id === id)
  if (!g) return
  const open = g.open === true ? (g.closes ? `Open now, closes ${clock(local(g.closes).min)} (before you're back)` : 'Open for your whole stay') : g.phone ? 'Hours unknown, so call ahead' : 'Hours unknown'
  sheet(`<div class="sheet-head"><span class="badge badge-garage">P</span><div><h2>${esc(g.name)}</h2><p class="muted">${esc(g.addr)}</p></div></div>
    <p class="verdict ok">${g.est !== null ? `About ${money(g.est)} for your stay` : g.op === 'ParkMobile' ? 'Price and hours are in the ParkMobile app' : g.rates.length ? "These rates don't say what your stay costs" : 'Price not listed'}</p>
    <p class="muted">${esc(open)} · ${walkMin(g.walk)} min walk${g.spaces ? ` · ${g.spaces} spaces` : ''}${g.op ? ` · ${esc(g.op)}` : ''}</p>
    ${g.rates.length ? `<p class="field-label">Rates</p><ul class="plain">${g.rates.map((r) => `<li>${esc(cents(r))}</li>`).join('')}</ul>` : ''}
    ${g.hours.length ? `<p class="field-label">Hours</p><ul class="plain">${g.hours.map((r) => `<li>${esc(r)}</li>`).join('')}</ul>` : ''}
    <div class="sheet-actions"><a class="button block suite-primary" ${goAttrs(g, g.name)}>${navLabel()}</a>
    <div class="two">${g.url ? `<a class="quiet button" href="${esc(g.url)}" target="_blank" rel="noopener">Website</a>` : ''}${g.phone ? `<a class="quiet button" href="tel:${esc(g.phone.replace(/[^\d+]/g, ''))}">Call</a>` : ''}</div></div>
`)
}

// ---------------------------------------------------------------- drive the loop

let driveCtx = null
const backTo = (d) => (d.back?.startsWith('#plan') ? d.back : `#plan?lat=${d.plan.at.lat}&lon=${d.plan.at.lon}`)
let onVisible = null // the current screen's "you're back from Maps" refresh

async function driveScreen() {
  teardown?.()
  teardown = null
  const d = drive()
  if (!d?.plan?.candidates?.length) { location.hash = ''; return }
  const { plan, win } = d
  const i = Math.min(d.i, plan.candidates.length)
  const done = i >= plan.candidates.length
  const c = plan.candidates[i]
  const g = plan.garages[0]

  if (done) {
    app.innerHTML = `<section class="drive">
      <div class="drive-top"><a class="back" href="${esc(backTo(d))}" aria-label="Back to the list">‹</a><span class="step">All ${plan.candidates.length} taken</span><span></span></div>
      <h1>Time for the garage</h1>
      ${g ? `<div class="list">${plan.garages.slice(0, 3).map(garageRow).join('')}</div>` : '<p class="muted">We don\'t know a garage close by.</p>'}
      <p class="fine">Sorry, they were all taken.</p>
    </section>`
    app.querySelectorAll('[data-garage]').forEach((b) => (b.onclick = () => openGarage(plan, Number(b.dataset.garage), win)))
    setBar(g ? `<div class="bar-inner"><a class="primary button" ${goAttrs(g, g.name)}>${navLabel(short(g.name))}</a></div>
      <p class="fine"><button type="button" class="linkbtn" id="restart">Go around again</button></p>` : `<div class="bar-inner"><a class="primary button" href="#">New search</a></div>`)
    $('restart')?.addEventListener('click', () => { saveDrive({ ...d, i: 0 }); driveScreen() })
    return
  }

  const verdict = c.wait > 0 ? `Opens ${when(c.from, win.start)}` : c.cost > 0 ? `About ${money(c.cost)} for your stay` : 'Free for your whole stay'
  const stale = Date.now() - d.t > 45 * MIN
  app.innerHTML = `<section class="drive">
    ${stale ? `<p class="busy">This loop was planned at ${esc(clock(local(d.t).min))}. Spots change, so <a href="${esc(backTo(d))}">refresh it</a>.</p>` : ''}
    <div class="drive-top"><a class="back" href="${esc(backTo(d))}" aria-label="Back to the list">‹</a>
      <span class="step">Spot ${i + 1} of ${plan.candidates.length}</span>
      <span class="dots" aria-hidden="true">${plan.candidates.map((_, k) => `<i class="${k < i ? 'past' : k === i ? 'cur' : ''}"></i>`).join('')}</span></div>
    <div class="mapbox small"><div id="dmap" class="map"></div></div>
    <div class="drive-card">
      <p class="arrived hidden" id="arrived" role="status"></p>
      <div class="sheet-head"><span class="badge badge-${look(c)}">${c.n}</span><div><h1>${esc(c.street)}</h1>
      <p class="muted">${esc([sideText(c), crossText(c.cross)].filter(Boolean).join(' · '))}</p></div></div>
      <p class="verdict ok">${esc(c.why)}</p>
      <p class="muted${/tow-away/.test(c.stopWhy || '') ? ' warn-text' : ''}">${esc([c.wait > 0 ? verdict : '', c.stopKind !== 'horizon' ? `Move by ${when(c.stopAt, win.start)}${c.stopWhy ? ` (${c.stopWhy.toLowerCase()})` : ''}` : 'No time limit', `${walkMin(c.walk)} min walk`, `about ${plural(c.spaces, 'space')}`].filter(Boolean).join(' · '))}</p>
      ${notesList(c.notes, 3)}
      ${plan.candidates[i + 1] ? `<p class="fine next">If it's taken, spot ${i + 2} is ${esc(driveText(c, plan.candidates[i + 1]))} away on ${esc(plan.candidates[i + 1].street)}.</p>` : g ? `<p class="fine next">Last one. If it's taken, the garage is ${esc(driveText(c, g))} away.</p>` : ''}
    </div>
  </section>`
  const cur = prefs().nav
  setBar(`<div class="bar-inner"><a class="primary button" id="navGo" ${goAttrs(c)}>${navLabel(`spot ${c.n}`)}</a></div>
    <div class="bar-inner two"><button type="button" class="quiet" id="taken">Taken, next ›</button><button type="button" class="cream" id="parkedHere">Parked here</button></div>
    <p class="fine navs">With ${Object.entries(NAV_APPS).map(([k, label]) => k === cur ? `<b>${label}</b>` : `<button type="button" class="linkbtn" data-nav="${k}">${label}</button>`).join(' · ')}</p>
    ${plan.candidates.length - i > 1 ? loopLine(plan.candidates.slice(i), g, i > 0) : ''}`)
  $('bar').querySelectorAll('[data-nav]').forEach((b) => (b.onclick = () => { savePrefs({ nav: b.dataset.nav }); driveScreen() }))
  $('taken').onclick = () => { report(c.id, 'taken'); saveDrive({ ...d, i: i + 1 }); driveScreen() }
  $('parkedHere').onclick = () => parkAt(c, win)

  // Spots with meter sensors: keep "2 of 3 open" fresh while you drive there.
  let timer = 0, gone = false
  let tick = async () => {}
  if (c.live) {
    tick = async () => {
      try {
        const fresh = await getCurb(c.id, win)
        const el = $('liveNote')
        if (gone || !el || !fresh.live) return
        el.textContent = fresh.live.vacant ? `${fresh.live.vacant} of ${fresh.live.total} meters open right now` : `All ${fresh.live.total} meters taken right now`
        el.classList.toggle('bad', !fresh.live.vacant)
      } catch {}
    }
    timer = setInterval(tick, 45_000)
  }
  // Back from Maps: if you're at this spot, ask the one question that matters.
  const arrived = async () => {
    const me = await quietly(4000, { fresh: true })
    if (!me || gone) return // moved on to the next spot (or left) while locating
    const m = Math.hypot((me.lat - c.lat) * 111320, (me.lon - c.lon) * 92300)
    const el = $('arrived')
    if (el && m < 60) {
      el.textContent = "You're here. Is there room?"
      el.classList.remove('hidden')
      $('bar')?.classList.add('bar-arrived')
    }
  }
  onVisible = () => { tick(); arrived() }
  arrived()
  teardown = () => { gone = true; clearInterval(timer); onVisible = null; driveCtx?.map.remove(); driveCtx = null }
  try {
    const m = await mapLib()
    const map = await m.curbMap($('dmap'), c, { me: lastKnown(), color: m.kindColor(look(c)) })
    if (gone) map.map.remove()
    else { driveCtx?.map.remove(); driveCtx = map }
  } catch {
    $('dmap')?.classList.add('hidden')
  }
}

// ---------------------------------------------------------------- parked

function parkAt(c, win) {
  report(c.id, 'parked')
  saveParked(parkedRecord(c, win))
  saveDrive(null)
  location.hash = 'parked'
}

let parkedCtx = null
let parkedGen = 0

/** The move-by card: "soon" within the hour of whichever deadline comes first, "tow" for a tow-away lane. */
function movebyClass(pk, now) {
  const due = meterFirst(pk, now) ? pk.meterOn : pk.stopAt
  return ['moveby', due && due - now < HOUR ? 'soon' : '', /tow-away/.test(pk.stopWhy || '') ? 'tow' : ''].filter(Boolean).join(' ')
}

async function parkedScreen() {
  teardown?.()
  teardown = null
  const pk = parked()
  if (!pk) { location.hash = ''; return }
  const now = Date.now()
  const c = pk.curb
  // Ticket odds from now to the end of the stay you planned (at least an hour, and inside the
  // server's 3-day maximum: a plan made for another day can end further out than that).
  // The odds cover the planned stay, or until a posted limit's move-by if that's later (at least an
  // hour, at most 12: "no limit" means the 72-hour rule, and 3 days of odds would mean nothing).
  const limit = pk.stopAt && pk.stopAt > now && pk.stopWhy !== '72-hour rule' ? Math.min(pk.stopAt, now + 12 * HOUR) : 0
  const win = { start: now, end: Math.min(now + 72 * HOUR - MIN, Math.max(now + HOUR, pk.win.end, limit)) }
  app.innerHTML = `<section class="parked">
    <p class="eyebrow">You're parked on</p>
    <h1>${esc(c.street || "an unnamed street")}</h1>
    <p class="muted">${esc([sideText(c), crossText(c.cross)].filter(Boolean).join(' · '))} · since ${esc(clock(local(pk.at).min))}</p>
    <div class="${movebyClass(pk, now)}" id="moveby">${moveBy(pk, now)}</div>
    ${pk.cost > 0 ? `<p class="pay">Feed the meter: about ${money(pk.cost)} for the stay you planned.</p>` : ''}
    <div id="parkedOdds"><p class="loading"><span class="spinner"></span> Checking ticket odds…</p></div>
    <div class="mapbox small"><div id="pmap" class="map"></div></div>
    <div id="parkedSign"></div>
  </section>`
  parkedBar(pk)
  // A newer render (back to the app, signal back) replaces this one: only the latest answer applies,
  // or two would each reschedule the reminder and leave one that "I've left" can't cancel.
  const gen = ++parkedGen
  getCurb(c.id, win, pk.at).then((d) => {
    if (gen !== parkedGen || !location.hash.startsWith('#parked')) return
    // The server looks a week ahead (the plan only looked a day past your stay).
    const v = d.verdict
    pk.stopAt = v.stopKind === 'horizon' ? null : v.stopAt
    pk.stopWhy = v.stopKind === 'horizon' ? '' : v.stopWhy
    pk.meterOn = v.meterOn ?? null
    pk.meterSince = v.meterSince ?? null
    pk.meterOff = v.meterOff ?? null // when the running meter stops
    pk.meterOnEnd = v.meterOnEnd ?? null // when the next paid stretch ends
    pk.opensAt = v.wait > 0 ? v.from : null // parked a little before it opens
    pk.opensWhy = v.wait > 0 ? v.nowWhy : ''
    pk.checked = Date.now()
    saveParked(pk)
    rescheduleReminder(pk)
    $('moveby').innerHTML = moveBy(pk, Date.now())
    $('moveby').className = movebyClass(pk, Date.now())
    parkedBar(pk)
    $('parkedOdds').innerHTML = sweepLine(d) + (d.sweepWarn ? `<p class="warn">${esc(d.sweepWarn.text)}</p>` : '') + (d.events?.games?.length ? eventNote(d.events) : '') + oddsCard(d.risk, { title: "Ticket patrol while you're gone", win })
    $('parkedSign').innerHTML = `<p class="field-label">What the sign says</p>${signLines(d.lines)}<p class="fine">Rules can change. The posted sign wins.</p>`
  }).catch((e) => {
    if (gen !== parkedGen || !location.hash.startsWith('#parked')) return
    // Underground or out of signal: everything above still works from this phone.
    $('parkedOdds').innerHTML = navigator.onLine === false
      ? '<p class="fine">Offline. Your move-by time is saved on this phone; ticket odds come back with signal.</p>'
      : `<p class="error">${esc(e.message)}</p>`
  })
  let gone = false
  teardown = () => { gone = true; parkedCtx?.map.remove(); parkedCtx = null }
  try {
    const m = await mapLib()
    const map = await m.curbMap($('pmap'), c, { me: lastKnown(), color: m.kindColor(look({ kind: c.kind, cost: pk.cost })) })
    if (gone) map.map.remove()
    else { parkedCtx?.map.remove(); parkedCtx = map }
  } catch {
    $('pmap')?.classList.add('hidden')
  }
}

function parkedBar(pk) {
  const c = pk.curb
  const now = Date.now()
  const alert = alertFor(pk)
  const canRemind = !!alert
  const ics = alert ? `/api/ics?t=${alert.at}&street=${encodeURIComponent(c.street)}&why=${encodeURIComponent(pk.stopWhy || '')}&lat=${c.lat}&lon=${c.lon}${alert.meter ? '&meter=1' : ''}` : null
  const rem = pk.reminder && pk.reminder.at > now ? pk.reminder : null
  const remindBtn = !canRemind ? '' : rem
    ? `<button type="button" class="quiet" id="remindOff" aria-label="Cancel the reminder">Reminder ${esc(clock(local(rem.at).min))} ✓</button>`
    : `<button type="button" class="primary" id="remindMe">Remind me</button>`
  setBar(`<div class="bar-inner">${remindBtn}<a class="${canRemind && !rem ? 'quiet' : 'primary'} button" href="${navUrl(c, 'walking')}" target="_blank" rel="noopener">Walk back</a></div>
    ${canRemind && !rem && iosBrowser() ? '<p class="fine">On iPhone, add parking to your Home Screen (Share → Add to Home Screen) to get a notification instead of a calendar event.</p>' : ''}
    <p class="fine">${rem && ics ? `<a class="linkbtn" href="${ics}">Add to calendar too</a> · ` : ''}<button type="button" class="linkbtn" id="shareCar">Share where I parked</button> · <button type="button" class="linkbtn" id="left">I've left. Clear this</button></p>`)
  $('shareCar').onclick = async (e) => {
    // A plain maps link works for anyone: no app, no account.
    const url = `https://maps.google.com/?q=${c.lat.toFixed(6)},${c.lon.toFixed(6)}`
    const text = `I'm parked on ${c.street}${c.side ? ` (${SIDE[c.side]})` : ''}.`
    try {
      if (navigator.share) await navigator.share({ title: 'Where I parked', text, url })
      else { await navigator.clipboard.writeText(`${text} ${url}`); e.target.textContent = 'Link copied' }
    } catch {}
  }
  // Both kinds of reminder come from the server: underground, say so instead of opening an error page.
  const noSignal = (b) => { b.textContent = 'Needs signal, try outside'; b.disabled = true; setTimeout(() => location.hash.startsWith('#parked') && parkedBar(pk), 4000) }
  $('remindMe')?.addEventListener('click', async (e) => {
    const b = e.currentTarget
    if (navigator.onLine === false) { noSignal(b); return }
    const done = busy(b, 'Setting…')
    // A notification 15 minutes before; where the browser can't, a calendar event with an alarm.
    const r = await remindPush(remindArgs(pk, alert)).catch(() => null)
    done()
    if (!r) {
      // The calendar file comes from the server too: "online" with no real signal (underground) would
      // open the browser's error page, so check first.
      const up = navigator.onLine !== false && await fetch('/api/push/key', { cache: 'no-store', signal: AbortSignal.timeout(4000) }).then((x) => x.ok).catch(() => false)
      if (up) location.href = ics
      else noSignal(b)
      return
    }
    pk.reminder = r
    saveParked(pk)
    parkedBar(pk)
  })
  $('remindOff')?.addEventListener('click', () => {
    cancelPush(rem.id)
    pk.reminder = null
    saveParked(pk)
    parkedBar(pk)
  })
  $('left').onclick = () => { cancelPush(pk.reminder?.id); saveParked(null); location.hash = '' }
}

// iPhone Safari only gets Web Push once the site is on the Home Screen.
const iosBrowser = () => /iPhone|iPad/.test(navigator.userAgent) && !navigator.standalone && !matchMedia('(display-mode: standalone)').matches

/** The server looked further ahead and the move-by time changed: move the reminder with it. */
function rescheduleReminder(pk) {
  if (!pk.reminder) return
  const a = alertFor(pk)
  // Still right if it's at either time this move-by allows (the evening one stays, even minutes before it fires).
  const night = !!a && !a.meter && nightBefore(a.at, pk.stopWhy)
  const fine = a && [remindAt(a.at, night), a.at - 15 * MIN, night ? fromLocal(addDays(local(a.at).date, -1), 20 * 60) : NaN].some((t) => Math.abs(pk.reminder.at - t) < 5 * MIN)
  if (fine) return
  cancelPush(pk.reminder.id)
  pk.reminder = null
  if (!a || typeof Notification === 'undefined' || Notification.permission !== 'granted') return saveParked(pk)
  remindPush(remindArgs(pk, a))
    .then((r) => { pk.reminder = r; saveParked(pk); if (location.hash.startsWith('#parked')) parkedBar(pk) })
    .catch(() => saveParked(pk))
}

// ---------------------------------------------------------------- check a spot

let checkGen = 0 // the newest check-screen load; an older one (a duration changed mid-load) stays quiet

async function checkScreen() {
  teardown?.()
  teardown = null
  setBar(null)
  const gen = ++checkGen
  const current = () => gen === checkGen && location.hash.startsWith('#check')
  const p = prefs()
  app.innerHTML = `<section class="check">
    <div class="plan-head"><a class="back" href="#" aria-label="Back">‹</a><div class="plan-title"><h1>Can I park here?</h1><p class="plan-sub">The curb where you're standing</p></div></div>
    <p class="field-label">For how long</p>
    ${durChips(p.dur)}
    <div id="checkBody"><p class="loading"><span class="spinner"></span> Finding you…</p></div>
  </section>`
  wireChips(app, (dur) => { savePrefs({ dur }); checkScreen() })
  const win = windowFor(p.dur)
  let me
  try {
    me = await locate({ fresh: true })
  } catch (e) {
    // There's no search box here: checking a curb needs where you're standing.
    const msg = e.message.replace(/,? or search for a place\.$|\. Search for a place instead\.$/, '.')
    if (current()) $('checkBody').innerHTML = `<p class="error">${esc(msg)}</p><p class="fine">Checking a curb needs your location. To look somewhere else, <a href="#">search for a place</a>.</p>`
    return
  }
  try {
    const h = await getHere(me, win)
    if (!current()) return
    const rows = h.curbs.map((d, i) => {
      const v = d.verdict
      const { head, sub } = checkWords(v, win)
      const tone = v.ok ? 'ok' : legalNow(v) ? 'short' : 'no'
      return `<button type="button" class="card tap check-row ${tone}" data-i="${i}">
        <span class="badge badge-${tone}" aria-hidden="true">${h.curbs.length > 1 ? i + 1 : { ok: '✓', short: '!', no: '×' }[tone]}</span>
        <span class="who"><span class="where">${esc(d.curb.street || "Unnamed street")}${d.curb.side ? ` <span class="side">${SIDE[d.curb.side]}</span>` : ''}</span>
        <span class="what">${esc(head)}</span>${sub ? `<span class="note">${esc(sub)} · ${d.dist} m away</span>` : `<span class="note">${d.dist} m away</span>`}</span>
        <span class="chev" aria-hidden="true">›</span></button>`
    }).join('')
    $('checkBody').innerHTML = `
      ${me.acc > 40 && h.curbs.length ? `<p class="busy">Your location is fuzzy (±${Math.round(me.acc)} m), so pick the curb you're actually on.</p>` : ''}
      ${h.curbs.length ? `<p class="summary">${h.curbs.length > 1 ? 'Which curb are you on?' : 'The curb next to you:'}</p>
        <div class="mapbox small"><div id="hmap" class="map" aria-label="The curbs around you"></div></div>
        <div class="list">${rows}</div>` : `<p class="empty">Can't tell for this spot (location accurate to ${Math.round(me.acc)} m), so read the posted sign.</p>`}
      ${areaSweep(h.sweep)}
      ${eventNote(h.events)}
      ${oddsCard(h.risk, { win })}
      <p class="fine sources">The posted sign wins.</p>`
    if (h.curbs.length) {
      const tones = h.curbs.map((d) => (d.verdict.ok ? 'ok' : legalNow(d.verdict) ? 'short' : 'no'))
      mapLib().then((m) => m.hereMap($('hmap'), me, h.curbs.map((d, i) => ({ ...d.curb, tone: tones[i] }))))
        .then((c) => { if (current()) teardown = () => c.map.remove(); else c.map.remove() })
        .catch(() => $('hmap')?.closest('.mapbox')?.classList.add('hidden'))
    }
    $('checkBody').querySelectorAll('[data-i]').forEach((b) => (b.onclick = () => {
      const d = h.curbs[Number(b.dataset.i)]
      const v = d.verdict
      const words = checkWords(v, win)
      sheet(`<div class="sheet-head"><span class="badge badge-${v.ok ? 'ok' : 'no'}">${v.ok ? '✓' : '×'}</span><div><h2>${esc(d.curb.street || "Unnamed street")}</h2>
        <p class="muted">${esc([d.curb.side ? SIDE[d.curb.side] : '', crossText(d.curb.cross)].filter(Boolean).join(' · '))}</p></div></div>
        <p class="verdict ${v.ok ? 'ok' : 'no'}">${esc(words.head)}</p>${words.sub ? `<p class="muted">${esc(words.sub)}</p>` : ''}
        ${sweepLine(d)}
        <p class="field-label">${esc(stripLabel(d.day))}</p>${dayStrip(d.day, win)}
        ${curbOdds(d.risk, d.riskHours, d.day, win)}
        <p class="field-label">What the sign says</p>${signLines(d.lines)}
        <div class="sheet-actions">${v.ok ? '<button type="button" class="block suite-primary" id="parkHere">I parked here</button>' : `<a class="button block suite-primary" href="#plan?lat=${me.lat.toFixed(6)}&lon=${me.lon.toFixed(6)}&here=1">Find somewhere legal nearby</a>`}</div>
        <p class="fine">Rules can change. The posted sign wins.</p>`)
      if (v.ok) $('parkHere').onclick = () => { $('sheet').close(); parkAt({ ...d.curb, stopAt: v.stopAt, stopWhy: v.stopWhy, stopKind: v.stopKind, cost: v.cost }, win) }
      wireCurbOdds($('sheetBody'), win)
    }))
  } catch (e) {
    if (current()) $('checkBody').innerHTML = `<p class="error">${esc(e.message)}</p>`
  }
}
const legalNow = (v) => v.nowKind === 'free' || v.nowKind === 'paid' || v.nowKind === 'limit'

/** "No: commercial loading until 6pm", or "Only until 5:50pm" when it's legal now but not for long enough. */
/** "Yes, free until 8am" / "Then the meter, move by 10am (2 hr limit)" for a curb on the check screen. */
function checkWords(v, win) {
  // Free now, with the meter starting before the move-by: say when it starts, then the move-by.
  const meterLater = v.ok && v.cost === 0 && v.meterOn && (v.stopKind === 'horizon' || v.meterOn < v.stopAt)
  const head = v.ok ? (v.cost > 0 ? `Yes, about ${money(v.cost)}` : meterLater ? `Yes, free until ${when(v.meterOn, win.start)}` : 'Yes, free') : notNow(v, win)
  const moveText = v.stopKind === 'horizon' ? 'no time limit' : `move by ${when(v.stopAt, win.start)} (${v.stopWhy.toLowerCase()})`
  const sub = v.ok ? (meterLater ? `Then the meter, ${moveText}` : moveText[0].toUpperCase() + moveText.slice(1)) : v.stopWhy && legalNow(v) ? `${v.stopWhy}` : ''
  return { head, sub }
}

function notNow(v, win) {
  if (legalNow(v)) return `Only until ${when(v.stopAt, win.start)}`
  const why = (v.nowWhy || 'no parking').toLowerCase()
  return `No: ${why}${v.opensAt ? ` until ${when(v.opensAt, Date.now())}` : ''}`
}

// ---------------------------------------------------------------- router

function route() {
  teardown?.()
  teardown = null
  if ($('sheet').open) $('sheet').close()
  const [name, qs] = location.hash.slice(1).split('?')
  const params = new URLSearchParams(qs || '')
  window.scrollTo(0, 0)
  if (name === 'plan' && params.get('lat')) return planScreen(params)
  if (name === 'drive') return driveScreen()
  if (name === 'parked') return parkedScreen()
  if (name === 'check') return checkScreen()
  return home()
}
window.addEventListener('hashchange', route)
registerWorker()
// Real phones are where bugs live: send uncaught errors to the Worker's logs (a handful per page).
let reported = 0
const sendError = (msg, stack) => {
  if (reported++ >= 5) return
  fetch('/api/clienterror', { method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
    body: JSON.stringify({ msg: String(msg).slice(0, 300), stack: String(stack ?? '').slice(0, 1500), screen: location.hash.split('?')[0] || '#' }) }).catch(() => {})
}
window.addEventListener('error', (e) => sendError(e.message, e.error?.stack))
window.addEventListener('unhandledrejection', (e) => sendError(e.reason?.message ?? e.reason, e.reason?.stack))
// Android Chrome offers installing as an app: put that one tap in the header, nothing more.
let installPrompt = null
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  installPrompt = e
  $('installBtn').classList.remove('hidden')
})
$('installBtn').onclick = async () => {
  if (!installPrompt) return
  installPrompt.prompt()
  await installPrompt.userChoice.catch(() => null)
  installPrompt = null
  $('installBtn').classList.add('hidden')
}
// Signal's back: the parked screen fetches what it couldn't (odds, the week ahead).
window.addEventListener('online', () => { retryCancels(); if (location.hash.startsWith('#parked')) parkedScreen() })
retryCancels()

// Tesla has no link to open: "Send to Tesla" puts the spot in the share sheet, where the Tesla app
// sends it to the car. Every driving link carries the spot (ui.js goAttrs); the map link is the fallback.
document.addEventListener('click', (e) => {
  const a = e.target.closest?.('a[data-go]')
  if (!a || prefs().nav !== 'tesla' || !navigator.share) return
  e.preventDefault()
  const [lat, lon] = a.dataset.go.split(',')
  navigator.share(shareSpot(lat, lon, a.dataset.goName)).catch(() => {})
})

// Coming back from Maps: refresh "move by" countdowns and live data.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return
  if (location.hash.startsWith('#parked')) parkedScreen()
  else onVisible?.()
})
route()
